import { useCallback, useEffect, useState } from "react";
import { Users, Share2, LogOut, Copy } from "lucide-react";
import { t } from "@/game/i18n";
import { Pack, createPack, fetchPack, joinPack, leavePack, loadName, saveName } from "@/game/api";
import { loadPackCode, playerId, savePackCode } from "@/game/storage";
import { packInviteUrl, sharePackInvite } from "@/game/share";

export const fmtCode = (code: string) => `${code.slice(0, 4)}-${code.slice(4)}`;

export const PackTable = ({ pack, compact = false }: { pack: Pack; compact?: boolean }) => {
  const me = playerId();
  return (
    <div data-testid="pack-table" className="w-full">
      {!compact && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Users size={16} className="text-emerald-300" />
          <span className="eyebrow">{pack.name}</span>
          <span className="ml-auto text-xs text-stone-500">{t("packMembers", { n: pack.members.length })}</span>
        </div>
      )}
      <div className="grid text-[11px] uppercase tracking-wider text-stone-500 px-3 mb-1" style={{ gridTemplateColumns: "1.4rem 1fr 6rem 6rem" }}>
        <span /><span /><span className="text-right">{t("packClassic")}</span><span className="text-right">{t("packDaily")}</span>
      </div>
      <ol className="space-y-1">
        {pack.members.map((m, i) => (
          <li key={m.player_id} data-testid={`pack-row-${i + 1}`} className={`grid items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${m.player_id === me ? "bg-emerald-500/20 ring-1 ring-emerald-400/70" : "bg-white/[0.03]"}`} style={{ gridTemplateColumns: "1.4rem 1fr 6rem 6rem" }}>
            <span className="font-mono text-emerald-300/80 text-right">{i + 1}</span>
            <span className="truncate font-semibold text-stone-200">{m.name}{m.player_id === me && <span className="text-emerald-300/70 text-xs"> · {t("packYou")}</span>}</span>
            <span className="text-right font-mono text-amber-200">{m.best_score}<span className="text-[10px] text-stone-500"> {t("packLvl")} {m.best_level}</span></span>
            <span className="text-right font-mono text-sky-200">{m.daily_score}<span className="text-[10px] text-stone-500"> {t("packWave")} {m.daily_wave}</span></span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const usePack = (refreshKey = 0) => {
  const [pack, setPack] = useState<Pack | null>(null);
  const code = loadPackCode();
  useEffect(() => {
    if (!code) { setPack(null); return; }
    fetchPack(code).then(setPack).catch(() => { setPack(null); });
  }, [code, refreshKey]);
  return pack;
};

const InviteButton = ({ pack }: { pack: Pack }) => {
  const [state, setState] = useState<"idle" | "shared" | "copied">("idle");
  const onShare = async () => {
    try { setState(await sharePackInvite(pack.code, pack.name) === "shared" ? "shared" : "copied"); } catch { setState("idle"); }
    setTimeout(() => setState("idle"), 2500);
  };
  return (
    <button data-testid="pack-invite-button" onClick={onShare} className="btn-primary flex-1 flex items-center justify-center gap-2">
      <Share2 size={16} /> {state === "shared" ? t("shared") : state === "copied" ? t("linkCopied") : t("packInvite")}
    </button>
  );
};

export const PackModal = ({ onClose, joinCode }: { onClose: () => void; joinCode?: string | null }) => {
  const [pack, setPack] = useState<Pack | null>(null);
  const [loading, setLoading] = useState(!!loadPackCode());
  const [mode, setMode] = useState<"create" | "join">(joinCode ? "join" : "create");
  const [name, setName] = useState("");
  const [code, setCode] = useState(joinCode ?? "");
  const [nick, setNick] = useState(loadName);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const c = loadPackCode();
    if (!c || joinCode) { setLoading(false); return; }
    fetchPack(c).then(setPack).catch(() => savePackCode(null)).finally(() => setLoading(false));
  }, [joinCode]);

  const run = useCallback(async (fn: () => Promise<Pack>) => {
    setBusy(true); setError(null);
    try {
      const p = await fn();
      savePackCode(p.code); saveName(nick.trim().slice(0, 16)); setPack(p);
      window.history.replaceState(null, "", window.location.pathname);
    } catch (e: unknown) {
      const st = (e as { response?: { status?: number } }).response?.status;
      setError(st === 404 ? t("packNotFound") : st === 409 ? t("packFull") : t("packError"));
    } finally { setBusy(false); }
  }, [nick]);

  const onLeave = async () => {
    if (!pack) return;
    setBusy(true);
    try { await leavePack(pack.code, playerId()); } catch { /* ignore */ }
    savePackCode(null); setPack(null); setBusy(false);
  };

  const nickOk = nick.trim().length > 0;
  return (
    <div data-testid="pack-modal" className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-md animate-fade-in">
      <div className="modal-card animate-pop">
        <div className="modal-body">
          <span className="eyebrow text-emerald-300">{t("pack")}</span>
          <h2 className="title-h2 mt-1">{pack ? pack.name : joinCode ? t("packJoinTitle") : t("packTitle")}</h2>
          {loading && <div className="mt-4 text-sm text-stone-500">{t("loading")}</div>}
          {!loading && pack && (
            <>
              <div className="mt-3 flex items-center gap-2 text-sm text-stone-300 flex-wrap">
                <span className="hud-label">{t("packCode")}</span>
                <span data-testid="pack-code" className="font-mono font-bold text-emerald-200 tracking-widest">{fmtCode(pack.code)}</span>
                <button data-testid="pack-copy-link-button" className="lang-btn flex items-center gap-1" onClick={() => { navigator.clipboard.writeText(packInviteUrl(pack.code)); setCopied(true); setTimeout(() => setCopied(false), 2000); }}><Copy size={12} /> {copied ? t("linkCopied") : t("copyLink")}</button>
                <span className="ml-auto text-xs text-stone-500">{t("packMembers", { n: pack.members.length })}</span>
              </div>
              <div className="mt-4"><PackTable pack={pack} compact /></div>
            </>
          )}
          {!loading && !pack && (
            <>
              <p className="mt-2 text-sm text-stone-400">{joinCode ? t("packJoining", { p: fmtCode(joinCode) }) : t("packHint")}</p>
              {!joinCode && (
                <div className="flex gap-1 mt-4" role="group">
                  <button type="button" data-testid="pack-mode-create" className={`lang-btn ${mode === "create" ? "lang-btn-on" : ""}`} onClick={() => setMode("create")}>{t("packCreate")}</button>
                  <button type="button" data-testid="pack-mode-join" className={`lang-btn ${mode === "join" ? "lang-btn-on" : ""}`} onClick={() => setMode("join")}>{t("packJoin")}</button>
                </div>
              )}
              <div className="mt-4 space-y-2">
                <input data-testid="pack-nick-input" value={nick} maxLength={16} onChange={(e) => setNick(e.target.value)} onKeyDown={(e) => e.stopPropagation()} placeholder={t("packNick")} className="name-input w-full" />
                {mode === "create"
                  ? <input data-testid="pack-name-input" value={name} maxLength={24} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.stopPropagation()} placeholder={t("packNameLabel")} className="name-input w-full" />
                  : <input data-testid="pack-code-input" value={code} maxLength={9} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.stopPropagation()} placeholder={t("packCodeLabel")} className="name-input w-full font-mono tracking-widest" />}
              </div>
              {error && <div data-testid="pack-error" className="mt-2 text-sm text-red-400">{error}</div>}
            </>
          )}
        </div>
        <div className="modal-footer flex flex-col gap-2">
          {pack ? (
            <div className="flex gap-2">
              <InviteButton pack={pack} />
              <button data-testid="pack-leave-button" onClick={onLeave} disabled={busy} className="btn-ghost flex items-center gap-2" title={t("packLeave")}><LogOut size={16} /></button>
            </div>
          ) : !loading && (
            mode === "create"
              ? <button data-testid="pack-create-button" disabled={busy || !nickOk || !name.trim()} onClick={() => run(() => createPack(name.trim(), playerId(), nick.trim()))} className="btn-primary w-full">{busy ? "…" : t("packCreate")}</button>
              : <button data-testid="pack-join-button" disabled={busy || !nickOk || code.replace("-", "").trim().length < 8} onClick={() => run(() => joinPack(code.replace("-", "").trim(), playerId(), nick.trim()))} className="btn-primary w-full">{busy ? "…" : t("packJoin")}</button>
          )}
          <button data-testid="close-pack-button" onClick={onClose} className="btn-ghost w-full">{t("close")}</button>
        </div>
      </div>
    </div>
  );
};
